import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FileSpreadsheet, Search, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { inr } from "@/lib/calc";
import { statusOf } from "@/lib/status";
import { linesOf, paidOf, balanceOf, exportPaymentsXlsx } from "@/lib/payments";
import { VerifyToggle, LateTag } from "@/components/PaymentVerify";

const dmy = (d) => (d ? d.split("-").reverse().join("/") : "");

const FILTERS = [{ id: "all", label: "All" }, { id: "partial", label: "Partially Paid" }, { id: "admitted", label: "Admitted" }];

const Kpi = ({ label, value, testid, money }) => (
  <div className="rounded-lg border border-plum-900/10 bg-white px-5 py-4">
    <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="font-num mt-1.5 text-2xl font-semibold text-plum-950" data-testid={testid}>{money ? `₹ ${inr(value)}` : value}</div>
  </div>
);

const Lines = ({ r, inRange }) => (
  <ul className="space-y-1 text-xs">
    {linesOf(r).filter(inRange).map((l, i) => (
      <li key={i} className="flex items-center gap-2 whitespace-nowrap text-slate-600" data-testid={`pay-line-${r.receipt_no}-${i}`}>
        <VerifyToggle r={r} line={l} testid={`pay-verify-${r.receipt_no}-${l.src.kind}-${l.src.index}`} />
        <span><span className="font-medium text-plum-950">{l.kind}</span> · ₹ {inr(l.amount)} · {l.mode}{l.reference ? ` (${l.reference})` : ""} · {dmy(l.date)}<LateTag l={l} testid={`pay-late-${r.receipt_no}-${l.src.kind}-${l.src.index}`} /></span>
      </li>
    ))}
  </ul>
);

export default function Payments() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const { data = [], isLoading } = useQuery({ queryKey: ["receipts"], queryFn: () => api.get("/receipts").then((r) => r.data) });
  const inRange = (l) => (!from || l.date >= from) && (!to || l.date <= to);

  const matched = useMemo(() => {
    const s = q.trim().toLowerCase();
    return data
      .filter((r) => r.status === "partial" || r.status === "admitted")
      .filter((r) => status === "all" || r.status === status)
      .filter((r) => !s || [r.receipt_no, r.inputs.childName, r.inputs.parentName, r.inputs.contact, r.inputs.email].some((v) => (v || "").toLowerCase().includes(s)));
  }, [data, q, status]);
  const rows = matched.filter((r) => (!from && !to) || linesOf(r).some(inRange));

  const lines = rows.flatMap((r) => linesOf(r).filter(inRange));
  const collected = lines.reduce((t, l) => t + (Number(l.amount) || 0), 0);
  const pending = matched.reduce((t, r) => t + balanceOf(r), 0);
  const byMode = Object.entries(lines.reduce((m, l) => ((m[l.mode] = (m[l.mode] || 0) + (Number(l.amount) || 0)), m), {})).sort((a, b) => b[1] - a[1]);
  const tag = from || to ? `${from || "start"}_to_${to || "today"}` : new Date().toLocaleDateString("en-CA");

  return (
    <div data-testid="payments-page">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
        <div className="fade-up">
          <div className="text-xs uppercase tracking-[0.2em] text-plum-700">Accounts</div>
          <h1 className="font-display text-3xl font-semibold text-plum-950 sm:text-4xl">Payments</h1>
        </div>
        <Button onClick={() => exportPaymentsXlsx(rows, inRange, `payments_${tag}.xlsx`)} disabled={!rows.length} className="bg-plum-700 hover:bg-plum-800" data-testid="payments-export-btn">
          <FileSpreadsheet className="mr-2 h-4 w-4" />Download Excel
        </Button>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="Total collected" value={collected} money testid="kpi-collected" />
        <Kpi label="Balance pending" value={pending} money testid="kpi-pending" />
        <Kpi label="Partially paid" value={rows.filter((r) => r.status === "partial").length} testid="kpi-partial-count" />
        <Kpi label="Admitted" value={rows.filter((r) => r.status === "admitted").length} testid="kpi-admitted-count" />
      </div>

      {lines.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2" data-testid="payments-by-mode">
          <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs text-amber-900" data-testid="payments-unverified-count">
            Not verified: <b className="font-num">{lines.filter((l) => !l.verified).length}</b> of {lines.length}
          </span>
          {byMode.map(([m, v]) => (
            <span key={m} className="rounded-full border border-plum-900/10 bg-white px-3 py-1 text-xs text-slate-600" data-testid={`mode-total-${m.replace(/\W+/g, "-").toLowerCase()}`}>
              {m}: <b className="font-num text-plum-950">₹ {inr(v)}</b>
            </span>
          ))}
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, receipt no, phone, email" className="pl-9" data-testid="payments-search" />
        </div>
        <div className="flex gap-1 rounded-md border border-plum-900/10 bg-white p-1">
          {FILTERS.map((f) => (
            <button key={f.id} onClick={() => setStatus(f.id)} data-testid={`payments-filter-${f.id}`}
              className={`rounded px-3 py-1.5 text-sm transition-colors duration-150 ${status === f.id ? "bg-plum-900 text-white" : "text-slate-600 hover:bg-plum-900/5"}`}>{f.label}</button>
          ))}
        </div>
        <label className="text-xs text-slate-500">Paid from<Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 w-40" data-testid="payments-from" /></label>
        <label className="text-xs text-slate-500">to<Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 w-40" data-testid="payments-to" /></label>
        {(from || to) && <Button variant="ghost" onClick={() => { setFrom(""); setTo(""); }} data-testid="payments-clear-dates">Clear dates</Button>}
      </div>

      <div className="overflow-x-auto rounded-lg border border-plum-900/10 bg-white">
        <table className="w-full text-sm" data-testid="payments-table">
          <thead className="bg-plum-900/[0.03] text-left text-[11px] uppercase tracking-wider text-slate-500">
            <tr>{["Receipt", "Student", "Parent & contact", "Counsellor", "Status", "Payments", "Due", "Paid", "Balance", ""].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-plum-900/5 align-top" data-testid={`payments-row-${r.receipt_no}`}>
                <td className="font-num whitespace-nowrap px-4 py-3 font-medium text-plum-950">{r.receipt_no}</td>
                <td className="px-4 py-3"><div className="font-medium text-plum-950">{r.inputs.childName}</div><div className="text-xs text-slate-500">{r.inputs.grade}</div></td>
                <td className="px-4 py-3"><div>{r.inputs.parentName}</div><div className="text-xs text-slate-500">{r.inputs.contact}{r.inputs.email ? ` · ${r.inputs.email}` : ""}</div></td>
                <td className="px-4 py-3 text-slate-600">{r.inputs.counselorName}</td>
                <td className="px-4 py-3"><span className={`whitespace-nowrap rounded-full border px-2 py-0.5 text-xs ${statusOf(r).cls}`} data-testid={`payments-status-${r.receipt_no}`}>{statusOf(r).label}</span>
                  {r.status === "partial" && r.follow_up_date && <div className="mt-1 text-[11px] text-slate-500">Balance by {dmy(r.follow_up_date)}</div>}</td>
                <td className="px-4 py-3"><Lines r={r} inRange={inRange} /></td>
                <td className="font-num whitespace-nowrap px-4 py-3">₹ {inr(r.computed.atAdmission)}</td>
                <td className="font-num whitespace-nowrap px-4 py-3 text-emerald-700" data-testid={`payments-paid-${r.receipt_no}`}>₹ {inr(paidOf(r))}</td>
                <td className="font-num whitespace-nowrap px-4 py-3 text-orange-700" data-testid={`payments-balance-${r.receipt_no}`}>₹ {inr(balanceOf(r))}</td>
                <td className="px-4 py-3"><Button size="sm" variant="outline" onClick={() => nav(`/receipts/${r.id}`)} data-testid={`payments-view-${r.receipt_no}`}><Eye className="mr-1.5 h-3.5 w-3.5" />Receipt</Button></td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-500" data-testid="payments-empty">{isLoading ? "Loading…" : "No students match these filters."}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
